import {ChangeDetectionStrategy, Component, computed, inject, input, signal} from '@angular/core';
import {FEEDBACK_REASONS, PHONE_HREF, PROPERTY_SEARCH_URL, STRINGS} from './assistant.constants';
import {AssistantTurn, ChatStore} from './chat-store';
import {IconComponent} from './icon.component';
import {toPlainText} from './markdown.service';
import {BubblesComponent, MessageActionsComponent, SourceChipsComponent} from './messages.components';

/**
 * One assistant answer: full width, seal avatar. Status shimmer before the first token, then the
 * sanitized Markdown with a CSS caret while streaming, then sources and actions. Screen readers
 * get the finished answer once, from the log's live region (aria-busy while streaming).
 */
@Component({
  selector: 'app-aa-assistant-message',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [BubblesComponent, IconComponent, MessageActionsComponent, SourceChipsComponent],
  template: `
    @let t = turn();
    <div class="aa-msg-assistant" [class.aa-streaming]="t.state === 'streaming'">
      <img class="aa-avatar" src="assets/imgs/seal.png" alt="" width="28" height="28">
      <div class="aa-msg-body">
        <span class="aa-visually-hidden">{{ strings.saidPrefix }} </span>
        @if (t.state === 'pending' || (t.state === 'streaming' && !t.html)) {
          <div class="aa-status">
            <app-aa-icon name="search"/>
            <span class="aa-shimmer">{{ t.status ?? strings.searching }}</span>
          </div>
        } @else if (t.html) {
          <div class="aa-md" [innerHTML]="t.html" [attr.aria-busy]="t.state === 'streaming' ? 'true' : null"></div>
        }

        @if (t.state === 'stopped') {
          <p class="aa-note">{{ strings.stopped }}</p>
        }

        @if (t.state === 'error' && t.error) {
          <div class="aa-alert">
            <app-aa-icon name="alert"/>
            <div>
              {{ t.error.message }}
              @if (t.error.code === 'upstream_error') {
                <p class="aa-alert-help">
                  {{ strings.fallbackBefore }}<a [href]="propertySearchUrl" target="_blank" rel="noopener noreferrer">{{ strings.propertySearch }}<span class="aa-visually-hidden"> {{ strings.opensInNewTab }}</span></a>{{ strings.fallbackMiddle }}<a [href]="phoneHref">{{ strings.phone }}</a>.
                </p>
              }
              @if (t.error.retryable && isLatest()) {
                <button class="aa-text-btn" type="button" (click)="store.retry(t.id)">{{ strings.tryAgain }}</button>
              }
            </div>
          </div>
        }

        @if (t.state === 'complete' && t.bubbles.length) {
          <app-aa-bubbles [bubbles]="t.bubbles" [disabled]="store.busy()" (pick)="store.send($event)"/>
        }

        @if (t.state === 'complete' && t.sources.length) {
          <app-aa-source-chips [sources]="t.sources" [labelId]="t.id + '-sources'"/>
        }

        @if (t.state === 'complete' || (t.state === 'stopped' && t.text)) {
          <app-aa-message-actions
            [feedback]="t.feedback"
            [canRate]="!!t.messageId"
            [canRegenerate]="isLatest() && !store.busy()"
            [always]="isLatest()"
            [copied]="copied()"
            (copy)="copy()"
            (rate)="store.rate(t.id, $event)"
            (regenerate)="store.regenerate(t.id)"/>

          @if (t.feedback === 'down' && !t.reason) {
            <div class="aa-reasons" role="group" [attr.aria-label]="strings.whatWentWrong + ' (optional)'">
              <span aria-hidden="true">{{ strings.whatWentWrong }}</span>
              @for (reason of reasons; track reason.code) {
                <button class="aa-reason" type="button" (click)="store.giveReason(t.id, reason.code)">{{ reason.label }}</button>
              }
            </div>
          } @else if (t.feedback) {
            <p class="aa-note">{{ strings.thanks }}</p>
          }
        }
      </div>
    </div>
  `,
})
export class AssistantMessageComponent {
  readonly turn = input.required<AssistantTurn>();
  readonly isLatest = input(false);

  protected readonly store = inject(ChatStore);
  protected readonly strings = STRINGS;
  protected readonly reasons = FEEDBACK_REASONS;
  protected readonly propertySearchUrl = PROPERTY_SEARCH_URL;
  protected readonly phoneHref = PHONE_HREF;
  protected readonly copied = signal(false);
  private readonly plainText = computed(() => toPlainText(this.turn().html));

  protected async copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.plainText());
      this.copied.set(true);
      this.store.notify(STRINGS.copied);
      setTimeout(() => this.copied.set(false), 2000);
    } catch {
      // Clipboard blocked (permissions or insecure context): nothing else to do.
    }
  }
}
